import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Mail, Upload, Users, Send, CheckCircle, AlertCircle, Loader2,
  UserPlus, ArrowRight, Inbox, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

// Google icon SVG
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Microsoft icon SVG
const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
    <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
  </svg>
);

export default function InviteContacts() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('email'); // 'email' or 'paste'
  const [loading, setLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [pasteEmails, setPasteEmails] = useState('');
  const [importingPaste, setImportingPaste] = useState(false);
  const [inviteSent, setInviteSent] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState(new Set());

  // Check URL params for import results
  useEffect(() => {
    const imported = searchParams.get('imported');
    const error = searchParams.get('error');
    if (imported) {
      toast.success(`Imported ${imported} contacts from your email!`);
      loadImportResults();
    } else if (error) {
      const msgs = {
        invalid_state: 'Session expired. Please try again.',
        token_failed: 'Could not connect to your email. Please try again.',
        no_sent_messages: 'No sent messages found in your email.',
        import_failed: 'Import failed. Please try again.',
        access_denied: 'Access denied by Google. The app owner needs to publish the Google OAuth app to Production mode, or add your email as a test user in Google Cloud Console.',
        auth_failed: 'Authentication failed. Please try again.'
      };
      toast.error(msgs[error] || 'Something went wrong.');
    } else {
      loadImportResults();
    }
  }, [searchParams]);

  const loadImportResults = async () => {
    try {
      const data = await api.getContactImportResults();
      if (data.has_imported) {
        setImportResults(data);
        // Auto-select all uninvited contacts
        const uninvited = (data.unmatched_contacts || []).filter(c => !c.invite_sent);
        setSelectedContacts(new Set(uninvited.map(c => c.email)));
      }
    } catch (e) {
      // No results yet
    }
  };

  const handleGoogleImport = async () => {
    setLoading(true);
    try {
      const data = await api.getGoogleContactsAuthUrl();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (e) {
      toast.error('Could not start Google import. Check configuration.');
      setLoading(false);
    }
  };

  const handleMicrosoftImport = async () => {
    setLoading(true);
    try {
      const data = await api.getMicrosoftContactsAuthUrl();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (e) {
      toast.error('Could not start Microsoft import. Check configuration.');
      setLoading(false);
    }
  };

  const handlePasteImport = async () => {
    const emails = pasteEmails
      .split(/[\n,;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e.includes('@'));

    if (emails.length === 0) {
      toast.error('Please enter at least one email address.');
      return;
    }

    setImportingPaste(true);
    try {
      const data = await api.importContacts(
        emails.map(e => ({ name: e.split('@')[0].replace(/[._]/g, ' '), email: e }))
      );
      toast.success(`Imported ${emails.length} contacts!`);
      setPasteEmails('');
      await loadImportResults();
    } catch (e) {
      toast.error('Failed to import contacts.');
    } finally {
      setImportingPaste(false);
    }
  };

  const handleSendAllInvites = async () => {
    if (selectedContacts.size === 0) {
      toast.error('Select at least one contact to invite.');
      return;
    }
    setInviting(true);
    try {
      const data = await api.sendAllInvites(customMessage, Array.from(selectedContacts));
      setInviteSent(data);
      toast.success(data.message || `Sent ${data.sent} invitations!`);
      setSelectedContacts(new Set());
      await loadImportResults();
    } catch (e) {
      toast.error('Failed to send invitations.');
    } finally {
      setInviting(false);
    }
  };

  const toggleContact = (email) => {
    setSelectedContacts(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleAll = () => {
    const uninvited = (importResults?.unmatched_contacts || []).filter(c => !c.invite_sent);
    if (selectedContacts.size === uninvited.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(uninvited.map(c => c.email)));
    }
  };

  const uninvitedContacts = importResults?.unmatched_contacts?.filter(c => !c.invite_sent) || [];
  const uninvitedCount = uninvitedContacts.length;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-8 px-4 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="invite-page-title">
            Import Contacts & Send Invites
          </h1>
          <p className="text-muted-foreground mt-1">
            Import everyone you've emailed — we'll send them a personalized invitation to join ConnectMailAgent.
          </p>
        </div>

        {/* Tab Selector */}
        <div className="flex gap-2 bg-muted/50 rounded-lg p-1" data-testid="import-tabs">
          <button
            onClick={() => setTab('email')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
              tab === 'email' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-email-import"
          >
            <Inbox className="w-4 h-4" />
            Import from Email
          </button>
          <button
            onClick={() => setTab('paste')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
              tab === 'paste' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-paste-import"
          >
            <Upload className="w-4 h-4" />
            Paste Emails
          </button>
        </div>

        {/* Import from Email Tab */}
        {tab === 'email' && (
          <Card data-testid="email-import-section">
            <CardContent className="p-6 space-y-4">
              <div className="text-center space-y-2 pb-2">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-lg font-semibold">Connect Your Email</h2>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  We scan your <strong>sent folder</strong> to find everyone you've emailed. 
                  We only read email addresses — never your message content.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  variant="outline"
                  className="h-14 text-base gap-3 border-2 hover:border-primary/40 hover:bg-primary/5"
                  onClick={handleGoogleImport}
                  disabled={loading}
                  data-testid="google-import-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <GoogleIcon />}
                  Import from Gmail
                </Button>
                <Button
                  variant="outline"
                  className="h-14 text-base gap-3 border-2 hover:border-primary/40 hover:bg-primary/5"
                  onClick={handleMicrosoftImport}
                  disabled={loading}
                  data-testid="microsoft-import-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <MicrosoftIcon />}
                  Import from Outlook
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center pt-2">
                Secure OAuth connection. We never store your email password.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Paste Emails Tab */}
        {tab === 'paste' && (
          <Card data-testid="paste-import-section">
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <h2 className="text-lg font-semibold">Paste Email Addresses</h2>
                <p className="text-sm text-muted-foreground">
                  Paste email addresses separated by commas, semicolons, or one per line.
                </p>
              </div>
              <textarea
                className="w-full min-h-[120px] p-3 rounded-lg border border-border bg-background text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder={"john@example.com\njane@company.com\nbob@business.org"}
                value={pasteEmails}
                onChange={(e) => setPasteEmails(e.target.value)}
                data-testid="paste-emails-input"
              />
              <Button
                onClick={handlePasteImport}
                disabled={importingPaste || !pasteEmails.trim()}
                className="w-full"
                data-testid="paste-import-btn"
              >
                {importingPaste ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Importing...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Import Contacts</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Import Results */}
        {importResults && importResults.has_imported && (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-primary" data-testid="stat-imported">{importResults.imported_count}</p>
                  <p className="text-xs text-muted-foreground">Contacts Found</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-500" data-testid="stat-matched">{importResults.matched_users?.length || 0}</p>
                  <p className="text-xs text-muted-foreground">Already on Platform</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-500" data-testid="stat-to-invite">{uninvitedCount}</p>
                  <p className="text-xs text-muted-foreground">Ready to Invite</p>
                </CardContent>
              </Card>
            </div>

            {/* Send Invites Section */}
            {uninvitedCount > 0 && (
              <Card className="border-primary/20 bg-primary/5" data-testid="send-invites-section">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Send className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Send Invitations</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Send a personalized invite email to <strong>{selectedContacts.size}</strong> of <strong>{uninvitedCount}</strong> selected contacts.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground">Personal Message (optional)</label>
                    <Input
                      className="mt-1.5"
                      placeholder={`Hi! I'm using ConnectMailAgent for business networking. Join me!`}
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      data-testid="custom-message-input"
                    />
                  </div>

                  <Button
                    onClick={handleSendAllInvites}
                    disabled={inviting || selectedContacts.size === 0}
                    size="lg"
                    className="w-full gap-2"
                    data-testid="send-all-invites-btn"
                  >
                    {inviting ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Sending Invites...</>
                    ) : (
                      <><Sparkles className="w-5 h-5" /> Send {selectedContacts.size} Invitation{selectedContacts.size !== 1 ? 's' : ''}</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Invite Sent Result */}
            {inviteSent && (
              <Card className="border-emerald-500/30 bg-emerald-500/5" data-testid="invite-result">
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <p className="text-sm text-foreground">
                    <strong>{inviteSent.sent}</strong> invitation{inviteSent.sent !== 1 ? 's' : ''} sent successfully!
                    {inviteSent.skipped > 0 && <span className="text-muted-foreground"> ({inviteSent.skipped} skipped — already members)</span>}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Matched Users */}
            {importResults.matched_users?.length > 0 && (
              <Card data-testid="matched-users-section">
                <CardContent className="p-5">
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-emerald-500" />
                    Contacts Already on ConnectMailAgent
                  </h3>
                  <div className="space-y-2">
                    {importResults.matched_users.map((u, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40">
                        <div className="flex items-center gap-3">
                          {u.photo ? (
                            <img src={u.photo} alt="" className="w-9 h-9 rounded-full object-cover" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                              {u.name?.[0] || '?'}
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-foreground">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.profession}</p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="gap-1 text-xs h-8" data-testid={`connect-btn-${i}`}>
                          <UserPlus className="w-3.5 h-3.5" /> Connect
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Uninvited Contacts Preview */}
            {importResults.unmatched_contacts?.length > 0 && (
              <Card data-testid="unmatched-contacts-section">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Mail className="w-4 h-4 text-amber-500" />
                      Contacts to Invite ({importResults.unmatched_contacts.length})
                    </h3>
                    {uninvitedCount > 0 && (
                      <button
                        onClick={toggleAll}
                        className="text-xs text-primary hover:underline font-medium"
                        data-testid="toggle-all-btn"
                      >
                        {selectedContacts.size === uninvitedCount ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 max-h-[400px] overflow-y-auto">
                    {importResults.unmatched_contacts.slice(0, 100).map((c, i) => (
                      <div key={i} className={`flex items-center gap-3 py-2 px-2.5 rounded-lg transition-colors ${
                        c.invite_sent ? 'opacity-60' : 'hover:bg-muted/30 cursor-pointer'
                      }`}
                        onClick={() => !c.invite_sent && toggleContact(c.email)}
                        data-testid={`contact-item-${i}`}
                      >
                        {!c.invite_sent ? (
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            selectedContacts.has(c.email) ? 'bg-primary border-primary' : 'border-muted-foreground/30'
                          }`} data-testid={`contact-checkbox-${i}`}>
                            {selectedContacts.has(c.email) && (
                              <CheckCircle className="w-3.5 h-3.5 text-white" />
                            )}
                          </div>
                        ) : (
                          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        )}
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground flex-shrink-0">
                          {c.name?.[0]?.toUpperCase() || c.email?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground truncate">{c.name || c.email}</p>
                          <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                        </div>
                        {c.invite_sent ? (
                          <span className="text-xs text-emerald-500 flex-shrink-0">Invited</span>
                        ) : (
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {selectedContacts.has(c.email) ? 'Selected' : ''}
                          </span>
                        )}
                      </div>
                    ))}
                    {importResults.unmatched_contacts.length > 100 && (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        + {importResults.unmatched_contacts.length - 100} more contacts
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Empty State — no imports yet */}
        {!importResults?.has_imported && (
          <Card className="border-dashed" data-testid="empty-state">
            <CardContent className="p-8 text-center space-y-3">
              <Users className="w-10 h-10 text-muted-foreground/40 mx-auto" />
              <p className="text-muted-foreground text-sm">
                No contacts imported yet. Connect your Gmail or Outlook account above to get started.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
